import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Car, Plus, Pencil, Trash2, Euro, Wallet, CalendarCheck, ShieldAlert, X, Upload, Lock, Unlock, ShieldCheck, Star, MessageSquare, Camera, AlertTriangle, TrendingUp, Sparkles, FileText } from "lucide-react";
import api, { formatError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart } from "recharts";
import { PlansPanel } from "@/components/PlansPanel";
import { DisputesPanel } from "@/components/DisputesPanel";
import { AnalyticsPanel } from "@/components/AnalyticsPanel";
import { DynamicPricingPanel } from "@/components/DynamicPricingPanel";
import { RewardsPanel } from "@/components/RewardsPanel";
import { PromoCodesPanel } from "@/components/PromoCodesPanel";
import { TeamPanel } from "@/components/TeamPanel";
import { useFlags } from "@/hooks/useFlags";
import { islandsFor } from "@/lib/islands";
import { VehicleFormDialog } from "@/components/loueur/VehicleFormDialog";
import { VehiclesList } from "@/components/loueur/VehiclesList";
import { BookingsPanel } from "@/components/loueur/BookingsPanel";

const COUNTRIES = [{ v: "martinique", l: "Martinique" }, { v: "guadeloupe", l: "Guadeloupe" }, { v: "saint-martin", l: "Saint-Martin" }, { v: "sint-maarten", l: "Sint Maarten" }];
const DISAGREE_REASONS = ["damage", "late_return", "cleanliness", "payment", "behavior", "other"];
const EMPTY = { brand: "", model: "", plate: "", category: "SUV", transmission: "Automatique", fuel: "Essence", seats: 5, doors: 4, price_per_day: 50, deposit: 800, country: "martinique", city: "", description: "", features: [], photos: [], year: 2023, mileage: 0, air_conditioning: true, pickup_locations: [] };

const toISO = (d) => d.toISOString().split("T")[0];
function expandRanges(booked) {
  const days = [];
  booked.forEach((b) => {
    let cur = new Date(b.start_date);
    const end = new Date(b.end_date);
    while (cur < end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  });
  return days;
}

export default function LoueurDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const v2 = useFlags().v2_enabled;
  const v3 = useFlags().v3_enabled;
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [vehPoints, setVehPoints] = useState([]);
  // availability
  const [selVehicle, setSelVehicle] = useState("");
  const [bookedDays, setBookedDays] = useState([]);
  const [blocked, setBlocked] = useState([]);
  // reviews
  const [reviews, setReviews] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  // deposit capture
  const [captureFor, setCaptureFor] = useState(null);
  const [captureForm, setCaptureForm] = useState({ amount: "", photos: [] });
  const [scanning, setScanning] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [scanningCg, setScanningCg] = useState(false);
  const [photoSuggestions, setPhotoSuggestions] = useState([]);
  const [claimFor, setClaimFor] = useState(null);
  const [claimForm, setClaimForm] = useState({ type: "disagreement", reason: "", details: "", photos: [] });

  const loadAll = () => {
    api.get("/loueur/vehicles").then((r) => setVehicles(r.data.vehicles)).catch(() => {});
    api.get("/loueur/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
    api.get("/loueur/revenue").then((r) => setRevenue(r.data)).catch(() => {});
    api.get("/loueur/connect/status").then((r) => setKyc(r.data)).catch(() => {});
    api.get("/loueur/reviews").then((r) => setReviews(r.data.reviews)).catch(() => {});
    api.get("/loueur/stats").then((r) => setStatsData(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const loadAvailability = (vid) => {
    setSelVehicle(vid);
    if (!vid) { setBookedDays([]); setBlocked([]); return; }
    api.get(`/loueur/vehicles/${vid}/availability`).then((r) => {
      setBookedDays(expandRanges(r.data.booked || []));
      setBlocked((r.data.blocked_dates || []));
    }).catch(() => {});
  };
  const isBooked = (day) => bookedDays.some((d) => toISO(d) === toISO(day));
  const toggleBlock = (day) => {
    if (isBooked(day) || day < new Date(new Date().toDateString())) return;
    const iso = toISO(day);
    setBlocked((b) => b.includes(iso) ? b.filter((x) => x !== iso) : [...b, iso]);
  };
  const saveBlocked = async () => {
    try { await api.put(`/loueur/vehicles/${selVehicle}`, { blocked_dates: blocked }); toast.success(t("dash.availabilitySaved")); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!dialog || !form.country) return;
    api.get(`/pickup-locations/${form.country}`).then((r) => setVehPoints(r.data.points || [])).catch(() => setVehPoints([]));
  }, [dialog, form.country]);

  const openLoueurClaim = (b) => { setClaimFor(b); setClaimForm({ type: "disagreement", reason: "", details: "", photos: [] }); };
  const uploadClaimPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/bookings/${claimFor.id}/claim/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setClaimForm((f) => ({ ...f, photos: [...f.photos, data.url] }));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };
  const submitLoueurClaim = async () => {
    try {
      const { data } = await api.post(`/loueur/bookings/${claimFor.id}/claim`, { type: claimForm.type, reason: claimForm.type === "disagreement" ? claimForm.reason : "", details: claimForm.details, photos: claimForm.photos });
      toast.success(t("claims.sent"));
      setBookings((prev) => prev.map((x) => x.id === claimFor.id ? { ...x, loueur_claim: data.claim } : x));
      setClaimFor(null);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const sendClaimMessage = async (b, party, text) => {
    try {
      const { data } = await api.post(`/claims/${b.id}/${party}/message`, { text });
      setBookings((prev) => prev.map((x) => x.id === b.id ? { ...x, [`${party}_claim`]: { ...x[`${party}_claim`], messages: [...(x[`${party}_claim`].messages || []), data.message] } } : x));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const captureFromClaim = (b) => {
    setCaptureFor(b);
    setCaptureForm({ amount: "", photos: (b.loueur_claim?.photos || []).map((p) => fileUrl(p)) });
  };
  const relistBooking = async (b) => {
    try {
      await api.post(`/loueur/bookings/${b.id}/relist`);
      toast.success(t("claims.relistDone"));
      setBookings((prev) => prev.map((x) => x.id === b.id ? { ...x, status: "no_show", relisted: true } : x));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setPhotoSuggestions([]); setDialog(true); };
  const openEdit = (v) => { setEditing(v.id); setForm({ ...v, features: v.features || [], photos: v.photos || [], pickup_locations: v.pickup_locations || [] }); setPhotoSuggestions([]); setDialog(true); };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...f.photos, fileUrl(data.url)] }));
      toast.success("OK");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const scanPlate = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/loueur/vehicles/plate-ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.plate) { toast.error(t("plate.ocrFail")); return; }
      setForm((f) => ({ ...f, plate: data.plate }));
      toast.success(t("plate.scanned", { plate: data.plate }));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setScanning(false); e.target.value = ""; }
  };

  const suggestPhoto = async () => {
    if (!form.brand && !form.model) { toast.error(t("plate.brandFirst")); return; }
    setSuggesting(true);
    try {
      const { data } = await api.get("/loueur/vehicles/suggest-photo", { params: { brand: form.brand, model: form.model } });
      if (!data.photos?.length) { toast.error(t("plate.noPhoto")); return; }
      setPhotoSuggestions(data.photos);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };

  const pickSuggestion = (url) => {
    setForm((f) => (f.photos.includes(url) ? f : { ...f, photos: [...f.photos, url] }));
    setPhotoSuggestions((s) => s.filter((p) => p !== url));
    toast.success(t("plate.photoAdded"));
  };

  const scanRegistration = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setScanningCg(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/loueur/vehicles/registration-ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.brand && !data.model && !data.plate && !data.year) { toast.error(t("plate.cgFail")); return; }
      setForm((f) => ({ ...f, brand: data.brand || f.brand, model: data.model || f.model, year: data.year || f.year, plate: data.plate || f.plate }));
      toast.success(t("plate.cgFilled"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setScanningCg(false); e.target.value = ""; }
  };

  const save = async () => {
    try {
      const payload = { ...form, seats: Number(form.seats), doors: Number(form.doors), price_per_day: Number(form.price_per_day), deposit: Number(form.deposit), year: Number(form.year), mileage: Number(form.mileage) };
      if (editing) { await api.put(`/loueur/vehicles/${editing}`, payload); toast.success(t("dash.modified")); }
      else { await api.post("/loueur/vehicles", payload); toast.success(t("dash.published")); }
      setDialog(false); loadAll();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("dash.deleteConfirm"))) return;
    try { await api.delete(`/loueur/vehicles/${id}`); toast.success(t("dash.deleted")); loadAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const openCapture = (b) => { setCaptureFor(b); setCaptureForm({ amount: "", photos: [] }); };
  const uploadDamagePhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCaptureForm((f) => ({ ...f, photos: [...f.photos, fileUrl(data.url)] }));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const doCapture = async () => {
    const amount = captureForm.amount ? Number(captureForm.amount) : null;
    try {
      const { data } = await api.post(`/loueur/bookings/${captureFor.id}/deposit/capture`, { amount, photos: captureForm.photos });
      toast.success(`${t("dash.captured")}: ${data.captured} €`); setCaptureFor(null); loadAll();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const releaseDeposit = async (b) => {
    try { await api.post(`/loueur/bookings/${b.id}/deposit/release`); toast.success(t("dash.released")); loadAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const sendReply = async () => {
    try { await api.post(`/loueur/reviews/${replyFor.id}/reply`, { reply: replyText }); toast.success(t("dash.replied")); setReplyFor(null); setReplyText(""); loadAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const uploadLoueurCondition = async (b, phase, e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/bookings/${b.id}/photos/upload?phase=${phase}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("dash.photoUploaded"));
      setBookings((prev) => prev.map((x) => x.id === b.id ? { ...x, condition_photos: [...(x.condition_photos || []), { by: data.by, phase: data.phase, url: data.url }] } : x));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const resolveDispute = async (b) => {
    try { await api.post(`/loueur/bookings/${b.id}/dispute/resolve`); toast.success(t("dash.disputeResolved")); loadAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const stats = [
    { icon: Car, label: t("dash.statVehicles"), value: vehicles.length, sub: t("dash.of25") },
    { icon: CalendarCheck, label: t("dash.statBookings"), value: bookings.length, sub: "" },
    { icon: Euro, label: t("dash.grossRevenue"), value: `${revenue?.gross ?? 0} €`, sub: "" },
    { icon: Wallet, label: t("dash.netRevenue"), value: `${revenue?.net ?? 0} €`, sub: `${t("dash.commission")} ${revenue?.commission ?? 0} €` },
  ];

  const depositBadge = (b) => {
    const map = { authorized: ["bg-canard/15 text-canard-dark", t("dash.depositAuthorized")], captured: ["bg-red-100 text-red-600", t("dash.depositCaptured")], released: ["bg-gray-100 text-gray-500", t("dash.depositReleased")] };
    const m = map[b.deposit_status]; if (!m) return null;
    return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${m[0]}`}>{m[1]}</span>;
  };

  return (
    <div className="section-sand min-h-screen py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-ink">{t("dash.loueurTitle")}</h1>
            <p className="text-muted2 font-body flex items-center gap-2">{user?.company_name || user?.name}
              {revenue?.rating > 0 && <span data-testid="loueur-rating-badge" className="inline-flex items-center gap-0.5 text-emeraude-dark font-bold text-sm"><Star className="w-4 h-4 fill-emeraude text-emeraude" /> {revenue.rating} <span className="text-muted2 font-normal">· {revenue.reviews_count} {t("dash.reviewsTab").toLowerCase()}</span></span>}
            </p>
          </div>
          <Button data-testid="add-vehicle-button" onClick={openNew} className="rounded-full bg-emeraude hover:bg-emeraude-dark shadow-cta font-bold"><Plus className="w-4 h-4 mr-1" /> {t("dash.addVehicle")}</Button>
        </div>

        {kyc && !kyc.charges_enabled && (
          <div data-testid="kyc-warning" className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-6 h-6 text-yellow-600 shrink-0" />
              <p className="text-sm text-yellow-800 font-body font-semibold">{t("dash.kycWarning")}</p>
            </div>
            <Button size="sm" onClick={() => nav("/loueur/onboarding")} className="rounded-full bg-yellow-600 hover:bg-yellow-700 shrink-0">{t("dash.verify")}</Button>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 shadow-card border border-border">
              <s.icon className="w-6 h-6 text-canard mb-2" />
              <p className="text-2xl font-heading font-extrabold text-ink">{s.value}</p>
              <p className="text-xs text-muted2 font-semibold">{s.label} {s.sub}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="fleet">
          <TabsList className="bg-white rounded-full p-1 border border-border flex-wrap h-auto">
            <TabsTrigger value="fleet" data-testid="tab-fleet" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("dash.fleet")}</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("dash.availability")}</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("dash.bookings")}</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-loueur-claims" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("claims.tab")}</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("dash.reviewsTab")}</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("stats.tab")}</TabsTrigger>
            {v2 && <TabsTrigger value="litiges" data-testid="tab-litiges" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("litiges.tab")}</TabsTrigger>}
            {v2 && <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("analytics.tab")}</TabsTrigger>}
            {v2 && <TabsTrigger value="tarification" data-testid="tab-tarification" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("pricing.tab")}</TabsTrigger>}
            {v2 && <TabsTrigger value="promo" data-testid="tab-promo" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("promo.tab")}</TabsTrigger>}
            {v2 && <TabsTrigger value="equipe" data-testid="tab-equipe" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("team.tab")}</TabsTrigger>}
            <TabsTrigger value="fidelite" data-testid="tab-fidelite" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("rewards.tab")}</TabsTrigger>
            {v2 && <TabsTrigger value="forfaits" data-testid="tab-forfaits" className="rounded-full data-[state=active]:bg-canard data-[state=active]:text-white font-bold">{t("plans.tab")}</TabsTrigger>}
          </TabsList>

          <TabsContent value="fleet" className="mt-6">
            <VehiclesList vehicles={vehicles} onEdit={openEdit} onRemove={remove} />
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <div className="bg-white rounded-2xl shadow-card border border-border p-6">
              <select data-testid="availability-vehicle-select" value={selVehicle} onChange={(e) => loadAvailability(e.target.value)}
                className="w-full sm:w-80 h-11 rounded-xl border border-border bg-sand px-3 text-sm font-semibold mb-5">
                <option value="">{t("dash.selectVehicle")}</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.brand} {v.model} · {v.plate}</option>)}
              </select>
              {selVehicle && (
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="rounded-2xl border border-border p-2 bg-sand/40 w-fit">
                    <Calendar mode="default" numberOfMonths={1} onDayClick={toggleBlock}
                      modifiers={{ booked: bookedDays, blocked: blocked.map((d) => new Date(d)) }}
                      modifiersClassNames={{ booked: "!bg-canard !text-white !rounded-full opacity-90", blocked: "!bg-red-500 !text-white !rounded-full" }}
                      data-testid="availability-calendar" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <p className="text-sm text-muted2 font-body">{t("dash.blockHint")}</p>
                    <div className="flex gap-4 text-xs font-semibold">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-canard inline-block" /> {t("dash.bookedLegend")}</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> {t("dash.blockedLegend")}</span>
                    </div>
                    <Button data-testid="save-blocked-button" onClick={saveBlocked} className="rounded-full bg-canard hover:bg-canard-dark font-bold">{t("dash.saveBlocked")}</Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <BookingsPanel bookings={bookings} depositBadge={depositBadge}
              onOpenCapture={openCapture} onRelease={releaseDeposit}
              onUploadCondition={uploadLoueurCondition} onResolveDispute={resolveDispute} onClaim={openLoueurClaim} onClaimMessage={sendClaimMessage} onCaptureFromClaim={captureFromClaim} onRelist={relistBooking} />
          </TabsContent>

          <TabsContent value="claims" className="mt-6">
            {bookings.filter((b) => b.client_claim?.status === "open" || b.loueur_claim?.status === "open").length === 0 ? (
              <div data-testid="loueur-claims-empty" className="text-center py-12 bg-white rounded-2xl border border-border text-muted2 font-body">{t("claims.emptyOpen")}</div>
            ) : (
              <div data-testid="loueur-claims-panel">
                <BookingsPanel bookings={bookings.filter((b) => b.client_claim?.status === "open" || b.loueur_claim?.status === "open")} depositBadge={depositBadge}
                  onOpenCapture={openCapture} onRelease={releaseDeposit}
                  onUploadCondition={uploadLoueurCondition} onResolveDispute={resolveDispute} onClaim={openLoueurClaim} onClaimMessage={sendClaimMessage} onCaptureFromClaim={captureFromClaim} onRelist={relistBooking} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            {reviews.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-border"><Star className="w-12 h-12 text-border mx-auto mb-3" /><p className="text-muted2 font-body">{t("dash.noReviews")}</p></div>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`loueur-review-${r.id}`} className="bg-white rounded-2xl shadow-card border border-border p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-heading font-bold text-ink text-sm">{r.author_name} <span className="text-xs text-muted2 font-body">· {r.vehicle_name}</span></p>
                        <div className="flex gap-0.5 mt-1">
                          {[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < r.rating ? "fill-emeraude text-emeraude" : "text-border"}`} />)}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" data-testid={`reply-review-${r.id}`} onClick={() => { setReplyFor(r); setReplyText(r.reply || ""); }} className="rounded-full h-8 text-xs">
                        <MessageSquare className="w-3.5 h-3.5 mr-1" /> {r.reply ? t("dash.editReply") : t("dash.reply")}
                      </Button>
                    </div>
                    {r.comment && <p className="text-muted2 font-body text-sm mt-2">{r.comment}</p>}
                    {r.reply && (
                      <div className="mt-2 pl-3 border-l-2 border-emeraude/40 bg-emeraude/5 rounded-r-lg p-2">
                        <p className="text-xs font-bold text-emeraude-dark">{t("dash.loueurReply")}</p>
                        <p className="text-sm text-muted2 font-body">{r.reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="stats" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl shadow-card border border-border p-5">
                <p className="font-heading font-bold text-ink flex items-center gap-2 mb-4"><Euro className="w-5 h-5 text-canard" /> {t("stats.revenueTitle")}</p>
                {statsData?.monthly?.some((m) => m.gross > 0) ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={statsData.monthly} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8EFEF" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={(m) => m.slice(5) + "/" + m.slice(2, 4)} tick={{ fontSize: 11, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v, n) => [`${v} €`, n === "gross" ? t("stats.gross") : t("stats.net")]} labelFormatter={(m) => m} contentStyle={{ borderRadius: 12, border: "1px solid #E8EFEF", fontSize: 12 }} />
                      <Bar dataKey="gross" name="gross" fill="#2D7E7E" radius={[6, 6, 0, 0]} maxBarSize={26} />
                      <Bar dataKey="net" name="net" fill="#3EB489" radius={[6, 6, 0, 0]} maxBarSize={26} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted2 font-body py-16 text-center">{t("stats.noData")}</p>}
              </div>

              <div className="bg-white rounded-2xl shadow-card border border-border p-5">
                <p className="font-heading font-bold text-ink flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-canard" /> {t("stats.occupancyTitle")}</p>
                {statsData?.monthly?.some((m) => m.occupancy > 0) ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <ComposedChart data={statsData.monthly} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8EFEF" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={(m) => m.slice(5) + "/" + m.slice(2, 4)} tick={{ fontSize: 11, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
                      <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => [`${v}%`, t("stats.occupancy")]} contentStyle={{ borderRadius: 12, border: "1px solid #E8EFEF", fontSize: 12 }} />
                      <Bar dataKey="occupancy" name="occupancy" fill="#EAF6F2" radius={[6, 6, 0, 0]} maxBarSize={26} />
                      <Line type="monotone" dataKey="occupancy" stroke="#2D7E7E" strokeWidth={2.5} dot={{ r: 3, fill: "#2D7E7E" }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted2 font-body py-16 text-center">{t("stats.noData")}</p>}
              </div>
            </div>

            <div data-testid="pro-teaser" className="relative mt-4 overflow-hidden rounded-2xl border border-emeraude/30 bg-gradient-to-br from-canard to-emeraude-dark p-6 sm:p-8 text-white">
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
              <div className="absolute right-16 bottom-0 w-24 h-24 rounded-full bg-white/5" />
              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="max-w-xl">
                  <span className="inline-flex items-center gap-1 bg-white/20 backdrop-blur rounded-full px-2.5 py-0.5 text-[11px] font-extrabold tracking-wide"><Sparkles className="w-3.5 h-3.5" /> {t("stats.proBadge")}</span>
                  <h3 className="font-heading font-extrabold text-2xl mt-3">{t("stats.proTitle")}</h3>
                  <p className="text-white/85 font-body text-sm mt-2">{t("stats.proSub")}</p>
                </div>
                <div className="text-center shrink-0">
                  <p className="font-heading font-extrabold text-4xl">{t("stats.proPrice")}</p>
                  <Button disabled data-testid="pro-cta" className="mt-3 rounded-full bg-white text-canard-dark hover:bg-white font-bold opacity-90 cursor-not-allowed">{t("stats.proCta")}</Button>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="litiges" className="mt-6">
            <DisputesPanel onUpgrade={() => document.querySelector('[data-testid="tab-forfaits"]')?.click()} />
          </TabsContent>

          <TabsContent value="forfaits" className="mt-6">
            <PlansPanel />
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <AnalyticsPanel onUpgrade={() => document.querySelector('[data-testid="tab-forfaits"]')?.click()} />
          </TabsContent>

          <TabsContent value="tarification" className="mt-6">
            <DynamicPricingPanel onUpgrade={() => document.querySelector('[data-testid="tab-forfaits"]')?.click()} />
          </TabsContent>

          <TabsContent value="fidelite" className="mt-6">
            <RewardsPanel />
          </TabsContent>

          <TabsContent value="promo" className="mt-6">
            <PromoCodesPanel onUpgrade={() => document.querySelector('[data-testid="tab-forfaits"]')?.click()} />
          </TabsContent>

          <TabsContent value="equipe" className="mt-6">
            <TeamPanel onUpgrade={() => document.querySelector('[data-testid="tab-forfaits"]')?.click()} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!captureFor} onOpenChange={(o) => !o && setCaptureFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{t("dash.captureTitle")}</DialogTitle></DialogHeader>
          {captureFor && (
            <div className="space-y-4">
              <p className="text-sm text-muted2 font-body">{t("dash.captureHint")}</p>
              <div>
                <label className="text-xs font-bold uppercase text-muted2">{t("dash.captureAmountLabel")}</label>
                <Input data-testid="capture-amount" type="number" placeholder={`${captureFor.deposit}`} value={captureForm.amount} onChange={(e) => setCaptureForm({ ...captureForm, amount: e.target.value })} className="mt-1 h-11 rounded-xl bg-sand" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-muted2">{t("dash.damagePhotos")}</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {captureForm.photos.map((p, i) => (
                    <div key={p} className="relative w-20 h-16 rounded-lg overflow-hidden">
                      <img src={p} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setCaptureForm({ ...captureForm, photos: captureForm.photos.filter((_, j) => j !== i) })} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label data-testid="capture-photo-upload" className="w-20 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-canard">
                    <Upload className="w-5 h-5 text-canard" />
                    <input type="file" accept="image/*" onChange={uploadDamagePhoto} className="hidden" />
                  </label>
                </div>
              </div>
              <Button data-testid="capture-confirm-button" onClick={doCapture} className="w-full h-11 rounded-full bg-red-500 hover:bg-red-600 font-bold"><Lock className="w-4 h-4 mr-1" /> {t("dash.confirmCapture")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!replyFor} onOpenChange={(o) => !o && setReplyFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{t("dash.loueurReply")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Textarea data-testid="reply-textarea" value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t("dash.replyPlaceholder")} className="rounded-xl bg-sand min-h-28" />
            <Button data-testid="reply-submit-button" onClick={sendReply} className="w-full h-11 rounded-full bg-canard hover:bg-canard-dark font-bold">{t("dash.sendReply")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!claimFor} onOpenChange={(o) => !o && setClaimFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" /> {t("claims.declareTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-muted2">{t("claims.typeLabel")}</label>
              <select data-testid="loueur-claim-type" value={claimForm.type} onChange={(e) => setClaimForm({ ...claimForm, type: e.target.value, reason: "" })}
                className="mt-1 w-full h-11 rounded-xl border border-border bg-sand px-3 text-sm font-semibold">
                <option value="disagreement">{t("claims.typeDisagreement")}</option>
                <option value="no_show">{t("claims.typeNoShow")}</option>
              </select>
            </div>
            {claimForm.type === "disagreement" && (
              <div>
                <label className="text-xs font-bold uppercase text-muted2">{t("claims.reasonLabel")}</label>
                <select data-testid="loueur-claim-reason" value={claimForm.reason} onChange={(e) => setClaimForm({ ...claimForm, reason: e.target.value })}
                  className="mt-1 w-full h-11 rounded-xl border border-border bg-sand px-3 text-sm font-semibold">
                  <option value="">{t("claims.chooseReason")}</option>
                  {DISAGREE_REASONS.map((r) => <option key={r} value={r}>{t(`claims.d_${r}`)}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase text-muted2">{t("claims.detailsLabel")}</label>
              <Textarea data-testid="loueur-claim-details" value={claimForm.details} onChange={(e) => setClaimForm({ ...claimForm, details: e.target.value })} placeholder={t("claims.detailsPh")} className="mt-1 rounded-xl bg-sand min-h-24" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-muted2">{t("claims.photosLabel")}</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {claimForm.photos.map((p, i) => (
                  <div key={p} className="relative w-20 h-16 rounded-lg overflow-hidden">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setClaimForm({ ...claimForm, photos: claimForm.photos.filter((_, j) => j !== i) })} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label data-testid="loueur-claim-photo-upload" className="w-20 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-canard">
                  <Upload className="w-5 h-5 text-canard" />
                  <input type="file" accept="image/*" onChange={uploadClaimPhoto} className="hidden" />
                </label>
              </div>
            </div>
            <Button data-testid="loueur-claim-submit" disabled={claimForm.type === "disagreement" && !claimForm.reason} onClick={submitLoueurClaim} className="w-full h-11 rounded-full bg-destructive hover:bg-destructive/90 font-bold">{t("claims.submit")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <VehicleFormDialog
        open={dialog} onOpenChange={setDialog} editing={editing}
        form={form} setForm={setForm} vehPoints={vehPoints}
        photoSuggestions={photoSuggestions} suggesting={suggesting}
        scanning={scanning} scanningCg={scanningCg} v3={v3}
        onScanPlate={scanPlate} onScanRegistration={scanRegistration}
        onSuggestPhoto={suggestPhoto} onPickSuggestion={pickSuggestion}
        onUploadPhoto={uploadPhoto} onSave={save}
      />
    </div>
  );
}
